import React, { useState, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { Link } from '@reach/router';
import * as Yup from 'yup';
import classnames from 'classnames';
import { I18N, I18NContext, GlobalContext } from 'commons';
import { baseUrl, orgId, Ajax, getUrlParams, cookies } from 'utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import v from 'scss/v';

export default function EbLogin(props) {
  const i18n = React.useContext(I18NContext);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const [loginErrorMessage, setLoginErrorMessage] = useState(null);
  const { flags:{
  PUBLIC_PORTAL_HIDE_LOGIN_TO_MP,
  PUBLIC_PORTAL_FORCE_NEW_ACCOUNT_TO_UP
  }, universalProfileUrl } = useContext(GlobalContext);


  const [usernameIsInvalid, setUsernameIsInvalid] = useState(false);
  const [passwordIsInvalid, setPasswordIsInvalid] = useState(false);

  const [errorMessages, setErrorMessages] = useState([]);

  const usernameRef = useRef(null);
  const Container = useRef(null);

  const validationSchema = {
    username: Yup.string().required(),
    password: Yup.string().required()
  };

  const errors = [];

  const { showSignUp, enabledSSO, accessKey } = props;
  const qparam = (accessKey === '') ? '' : `?accessKey=${accessKey}`;

  const preCheck = (uname, pwd) => {
    if (!uname) {
      return false;
    }

    if (!pwd) {
      return false;
    }

    return true;
  };

  const onUsernameBlur = async () => {
    if (await validationSchema.username.isValid(username)) {
      setUsernameIsInvalid(false);
    } else {
      setUsernameIsInvalid(true);
      errors[0] = 'login.username.enterUsername';
    }
  };

  const onPasswordBlur = async () => {
    if (await validationSchema.password.isValid(password)) {
      setPasswordIsInvalid(false);
    } else {
      setPasswordIsInvalid(true);
      errors[1] = 'login.password.enterPassword';
    }
  };

  const updateErrors = () => {
    // queue setErrorMessages after validation by settimeout
    setTimeout(() => {
      setErrorMessages(errors);
      Container && Container.current && Container.current.focus();
    });
  };

  const onSubmit = () => {
    setLoginErrorMessage('');
    const params = window.location.search;
    const url = '/manage/login' + params;
    const data = new FormData();
    data.append('orgId', orgId);
    data.append('username', username);
    data.append('password', password);
    data.append('keepSigned', false);

    onUsernameBlur();
    onPasswordBlur();

    updateErrors();

    if (preCheck(username, password)) {
      Ajax.post(url, data).then(({ data: res }) => {
        const { redirect, status } = res;
        if (redirect) {
          const params = getUrlParams(window.location.search);
          cookies.set('accessKey', accessKey, {
            path: `/${orgId}`
          });
          const { origin: originUrl } = params;
          if (originUrl && originUrl !== '') {
            window.location.href = window.decodeURIComponent(originUrl);
          } else {
            const redirectUrl = `${baseUrl}${redirect}#/home`;
            window.location.href = redirectUrl;
          }
        } else if (status === '-1') {
          sessionStorage.setItem('p_expired', 'Y');
          window.location.href = `${baseUrl}/${orgId}/reset-password${params}`;
        } else {
          const { remainCount, fedrampLockTime } = res;
          let msg;

          if (remainCount) {
            msg = ['ssp.request.account.error.fedRamp', remainCount.toString()];
          } else if (fedrampLockTime) {
            msg = ['ssp.request.account.locked.fedRamp', fedrampLockTime.toString()];
          } else {
            msg = [res.errorCode];
          }
          setLoginErrorMessage(msg);
        }
      }).catch(err => {
        console.error('error:', err);
      });
    }
  };

  return (
    <div className={classnames({ 'hide-login': PUBLIC_PORTAL_HIDE_LOGIN_TO_MP })}>
    <form className="login-form-box " eb-test="ssp_login_form">
      <div className="row login-form-title">
        <h1 className="col-12 text-center">
          <I18N>ssp.title.login</I18N>
        </h1>
      </div>
      {
        loginErrorMessage &&
        <div className="row login-form-error">
          <div className="alert alert-danger w-100 mb-0" role="alert">
            <FontAwesomeIcon icon={faExclamationCircle} color={v('$red-7')} width="1em" height="1em" />
            <div className="ml-2">
              <b><I18N>home.alert.box.title.error</I18N></b>
              <p className="mb-0"><I18N>{loginErrorMessage.toString()}</I18N></p>
            </div>
          </div>
        </div>
      }
      <div className="row">
        <div>
          {
            !!errorMessages.length &&
            <div className="error-container" tabIndex="-1" ref={Container}>
              <p className="mb-1"><I18N>form.error.messages.group.title</I18N></p>
              <ul id="error-messages">
                {
                  Object.keys(errorMessages).map((name) => (<li key={name}><I18N>{errorMessages[name] ? errorMessages[name] : ''}</I18N></li>))
                }
              </ul>
            </div>
          }
        </div>
      </div>
      <div className="row login-form-item">
        <label htmlFor="username">
          <I18N>ssp.register.username</I18N>
          <span aria-hidden="true" className="required-star">*</span>
        </label>
        <div className="login-form-control">
          <input
            ref={usernameRef}
            className={classnames('form-control', { error: usernameIsInvalid })}
            type="text"
            aria-label={i18n['ssp.register.username']}
            name="username"
            value={username}
            onChange={({ target }) => { setUsername(target.value); }}
            onBlur={onUsernameBlur}
            autoComplete="off"
            eb-test="ssp_login_field_username"
            aria-invalid={usernameIsInvalid}
            id="username"
            required
            {...(usernameIsInvalid ? { 'aria-describedby': 'username-invalid-error' } : {})}
          />
        </div>
        {
          usernameIsInvalid &&
          <div className="form-error-msg" id="username-invalid-error" aria-live="assertive">
            <I18N>login.username.enterUsername</I18N>
          </div>
        }
      </div>
      <div className="row login-form-item">
        <label htmlFor="password">
          <I18N>ssp.register.password</I18N>
          <span aria-hidden="true" className="required-star">*</span>
        </label>
        <div className="login-form-control">
          <input
            className={classnames('form-control', { error: passwordIsInvalid })}
            type="password"
            aria-label={i18n['ssp.register.password']}
            name="password"
            value={password}
            onChange={({ target }) => { setPassword(target.value); }}
            onBlur={onPasswordBlur}
            autoComplete="new-password"
            eb-test="ssp_login_field_password"
            aria-invalid={passwordIsInvalid}
            id="password"
            required
            {...(passwordIsInvalid ? { 'aria-describedby': 'passowrd-invalid-error' } : {})}
          />
        </div>
        {
          passwordIsInvalid &&
          <div className="form-error-msg" id="passowrd-invalid-error" aria-live="assertive">
            <I18N>login.password.enterPassword</I18N>
          </div>
        }
      </div>
      <div className="row action-button-box">
        <button className="btn btn-primary btn-block" type="button" onClick={onSubmit} eb-test="ssp_login_button_submit">
          <I18N>ssp.login.login</I18N>
        </button>
      </div>
      <div className="action-links-box">
        <div className="action-links forgot-links row">
          <Link
            role="link"
            eb-test="ee_login_link_forgotUsername"
            to={`/${orgId}/forgot-username${qparam}`}
          >
            <I18N>ssp.title.forgotUsername</I18N>
          </Link>
          <span className="text-or">&nbsp;<I18N>text.or</I18N>&nbsp;</span>
          <Link
            role="link"
            eb-test="ee_login_link_forgotPassword"
            to={`/${orgId}/forgot-password${qparam}`}
          >
            <I18N>ssp.title.forgotPassword</I18N>
          </Link>
        </div>
        {
          showSignUp &&
                <div className="action-links row">
                  <span className="text-account-tip" id="change-1">
                    <I18N>login.haveNoAccount</I18N>&nbsp;
                  </span>
                  {PUBLIC_PORTAL_FORCE_NEW_ACCOUNT_TO_UP && universalProfileUrl ? (
                    <a
                      href={`${universalProfileUrl}?orgId=${orgId}`}
                      eb-test="ssp_login_link_signup"
                    >
                      <I18N>ssp.register.title</I18N>
                    </a>
                  ) : (
                    <Link
                      to={`/${orgId}/new`}
                      eb-test="ssp_login_link_signup"
                    >
                      <I18N>ssp.register.title</I18N>
                    </Link>
                  )}
                </div>
        }
        {
          enabledSSO &&
                <div className="action-links row">
                  <Link
                    role="link"
                    eb-test="ssp_login_link_sso"
                    to={`./sso${window.location.search}`}
                  >
                    <I18N>login.SSO.credentials</I18N>
                  </Link>
                </div>
        }
      </div>
    </form>
    </div>
  );
}

EbLogin.propTypes = {
  showSignUp: PropTypes.bool.isRequired,
  enabledSSO: PropTypes.bool.isRequired,
};
